import { useEffect, useState } from 'react';
import type { ComponentType } from 'react';
import { clsx } from 'clsx';
import { View } from 'react-native';
import { Text } from 'shared/components/text';

export type LocationRadiusCoordinate = {
  latitude: number;
  longitude: number;
};

export type LocationRadiusMapProps = {
  coordinate: LocationRadiusCoordinate | null;
  initialCenter?: LocationRadiusCoordinate | null;
  radiusInKm: number;
  unavailableLabel: string;
  instructionLabel: string;
  onCoordinateChange: (coordinate: LocationRadiusCoordinate) => void;
  onLocate: (coordinate: LocationRadiusCoordinate) => void;
  className?: string;
};

type LocationRadiusMapComponent = ComponentType<LocationRadiusMapProps>;

const containerClassName = 'relative h-56 w-full overflow-hidden rounded-2xl bg-stone-100 md:h-64';

function scheduleIdle(callback: () => void) {
  if (typeof window.requestIdleCallback === 'function') {
    const idleId = window.requestIdleCallback(callback, { timeout: 750 });

    return () => window.cancelIdleCallback(idleId);
  }

  const timeoutId = window.setTimeout(callback, 0);

  return () => window.clearTimeout(timeoutId);
}

export function LocationRadiusMap(props: LocationRadiusMapProps) {
  const [MapComponent, setMapComponent] = useState<LocationRadiusMapComponent | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let mounted = true;

    const cancelIdle = scheduleIdle(() => {
      import('./location-radius-map-impl.web')
        .then((module) => {
          if (mounted) {
            setMapComponent(() => module.LocationRadiusMapImpl);
          }
        })
        .catch(() => {
          if (mounted) {
            setLoadFailed(true);
          }
        });
    });

    return () => {
      mounted = false;
      cancelIdle();
    };
  }, []);

  if (loadFailed) {
    return (
      <View
        className={clsx(containerClassName, 'items-center justify-center p-4', props.className)}
      >
        <Text className={'text-center text-sm text-stone-600'}>{props.unavailableLabel}</Text>
      </View>
    );
  }

  if (!MapComponent) {
    return <View className={clsx(containerClassName, props.className)} />;
  }

  return <MapComponent {...props} />;
}
